import { FormattedMessage, useIntl } from 'react-intl';
import { OnSubmitHandler, useInputGroup } from '@wikus-ui/hooks/useInputGroup';
import { OrderStockArticle, StockArticle } from '../../../model';
import { RequiredPositiveNumber } from '@wikus-ui/components/utils/validators';
import { WikusButton } from '@wikus-ui/components/core/button';
import { WikusConfirmation } from '@wikus-ui/components/core/confirmation';
import { WikusInputRow, WikusTextField } from '@wikus-ui/components/core/input';
import { useEffect, useState } from 'react';
import { useUser } from '../../../hooks/user';

type TStockArticleOrderFormProps = {
  article: StockArticle;
  onSubmit: (order: OrderStockArticle) => Promise<boolean>;
};

export const ArticleOrderForm = ({
  article,
  onSubmit,
}: TStockArticleOrderFormProps) => {
  const intl = useIntl();
  const user = useUser();
  const [confirmOrder, setConfirmOrder] = useState<{ amount: number }>();
  const OrderStockArticleInputGroup = `order-article-${article.id}`;

  const _onSubmit: OnSubmitHandler = async () => {
    if (!isValid) {
      return false;
    }

    const isSuccess = await onSubmit({
      id: article.id,
      amount: value.amount,
    });

    if (isSuccess) {
      setGroup({
        submitted: false,
        value: {
          amount: 1,
        },
      });
    }

    return isSuccess;
  };

  const { value, isValid, onSubmit: onSubmitInternal, createInputProps, isSubmitting, setGroup } =
    useInputGroup(OrderStockArticleInputGroup, _onSubmit, 'order-article');

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      setConfirmOrder({ amount: value.amount });
    }
  };

  const _onConfirmOrder = async () => {
    if (!confirmOrder) {
      return false;
    }

    // Create a synthetic event to pass to the handler
    const syntheticEvent = { preventDefault: () => {} } as React.FormEvent<HTMLFormElement>;
    const result = await onSubmitInternal(syntheticEvent);
    setConfirmOrder(undefined);
    return result;
  };

  const _onAbortOrder = () => {
    setConfirmOrder(undefined);
  };

  useEffect(() => {
    console.log('set article', article);
    setGroup({
      submitted: false,
      value: {
        amount: article.reorderAmount || 1,
      },
    });
  }, [article])

  return (
    <>
      <form onSubmit={handleFormSubmit}>
        <WikusInputRow>
          <WikusTextField
            type="number"
            {...createInputProps('amount', RequiredPositiveNumber, {
              labelParams: {
                unit: user.getUnit(value?.articles?.group === 'R'),
              },
            })}
            xs
          />

          <WikusButton type="submit" color="primary" disabled={isSubmitting || isValid === undefined}>
            {intl.formatMessage({
              id: 'customer-article.order.submit',
            })}
          </WikusButton>
        </WikusInputRow>
      </form>

      <WikusConfirmation
        title={intl.formatMessage({ id: 'customers.tabs.storage.confirmation.order' })}
        open={!!confirmOrder}
        onAbort={_onAbortOrder}
        onConfirm={_onConfirmOrder}>
        <FormattedMessage
          id="customers.tabs.storage.confirmation.order.question"
          values={{
            amount: confirmOrder?.amount,
            article: article.article.itemNumber,
          }}
        />
      </WikusConfirmation>
    </>
  );
}